import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ReceiptText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, IconChip } from '@/components/ui';
import { BILLS_KEY, purchasingApi } from '@/features/purchasing/purchasing-api';
import { intlLocale } from '@/lib/i18n';
import { cn, formatCurrency } from '@/lib/utils';

const dateFmt = new Intl.DateTimeFormat(intlLocale(), {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'numeric',
});
const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date());
const daysUntil = (d: string) =>
  Math.round((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000);

/** בסקירה: חשבוניות ספקים באיחור, לתשלום השבוע, ומה מחכה לאישור. */
export function PayablesWatch() {
  const { t } = useTranslation('dashboard');
  const list = useQuery({
    queryKey: BILLS_KEY,
    queryFn: ({ signal }) => purchasingApi.bills(signal),
  });
  const open = (list.data ?? []).filter((b) => b.status === 'RECEIVED' || b.status === 'APPROVED');
  const soon = open
    .filter((b) => b.dueDate && daysUntil(b.dueDate) <= 7)
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
    .slice(0, 5);
  const toApprove = open.filter((b) => b.status === 'RECEIVED').length;
  const late = soon.some((b) => b.dueDate && daysUntil(b.dueDate) < 0);

  return (
    <Card
      className={cn(late ? 'border-danger-border' : soon.length > 0 && 'border-warning-border')}
    >
      <CardHeader tone="pink" className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2.5">
          <IconChip icon={ReceiptText} tone="pink" variant="solid" size="sm" />
          {t('payables.title')}
        </CardTitle>
        <Link to="/supplier-invoices" className="text-xs font-medium text-accent hover:underline">
          {t('payables.viewAll')}
        </Link>
      </CardHeader>
      <CardContent className="space-y-2 pt-3">
        {list.isPending ? null : soon.length === 0 ? (
          <p className="text-sm text-fg-muted">
            {open.length ? t('payables.noneThisWeek') : t('payables.noneOpen')}
          </p>
        ) : (
          <ul className="space-y-1">
            {soon.map((b) => {
              const left = daysUntil(b.dueDate!);
              return (
                <li key={b.id}>
                  <Link
                    to="/supplier-invoices"
                    className="-mx-2 flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-surface-hover"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-fg">
                      {b.supplier?.name ?? t('payables.supplier')}
                    </span>
                    <span className="tabular shrink-0 text-sm text-fg">
                      {b.amount ? formatCurrency(Number(b.amount)) : ''}
                    </span>
                    <span
                      className={cn('shrink-0 text-xs', left < 0 ? 'text-danger' : 'text-warning')}
                    >
                      {left < 0
                        ? t('payables.late', { count: -left })
                        : left === 0
                          ? t('payables.today')
                          : t('payables.until', {
                              date: dateFmt.format(new Date(`${b.dueDate}T12:00:00Z`)),
                            })}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        {toApprove > 0 ? (
          <p className="text-xs text-fg-muted">{t('payables.toApprove', { count: toApprove })}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
